import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { entityTypes } from '@/shared/constants/constants';
import { productTypes } from '@/shared/constants/shopify';
import { queryPatternBySku } from '@/shared/graphql/gateway/queries';
import { productGroupIdByHandleQuery } from '@/shared/graphql/storefront/queries';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import { productStoreInternals } from '@/shared/pinia/modules/product-utils';
import { useUserStore } from '@/shared/pinia/modules/user';
import { Strategy } from '@/shared/strategies/index';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

type KitRelatedPattern = FlyQualifiedProduct & {
  productGroupId?: string | null;
};

export type ProductPrice = {
  initial_unit: number | null;
  unit: number | null;
  before: number | null;
  savings: number | null;
};

export const useProductStore = defineStore('product', () => {
  const productId = ref<string | null>(null);
  const productName = ref<string | null>(null);
  const variantId = ref<string | null>(null);
  const legacyId = ref<string | null>(null);
  const productGroupId = ref<string | null>(null);
  const strategy = ref<string>('');
  const purchasable = ref(true);
  const entityType = ref(entityTypes.PRODUCT);
  const mainImageUrl = ref<string | null>(null);
  const relatedPattern = ref<KitRelatedPattern | null>(null);
  const pointsPrice = ref<number | null>(null);
  const price = ref<ProductPrice>({
    initial_unit: null, // unit price without discount
    unit: null,
    before: null,
    savings: null
  });

  const {
    updateDOMPricesAndSavings,
    calculateNewPricesByQuantityChange,
    calculateNewPricesByVariantChange
  } = productStoreInternals();

  const isPattern = computed(() => entityType.value === entityTypes.PATTERN);
  const isProduct = computed(() => entityType.value === entityTypes.PRODUCT);
  const isKit = computed(() => entityType.value === entityTypes.KIT);

  const getStrategy = computed(() => strategy.value || Strategy.ProductPage);

  // NOTE: For kit products this falls back to productId — Fly Bundles products have no metafields and are not in Sanity, so legacyId and productGroupId are always empty.
  // Callers that need the correct kit entity ID (e.g. tab counters, user gallery) must go through getEntityIdFromContext(), which resolves relatedPattern.productGroupId instead.
  const resolvedEntityId = computed(
    () => legacyId.value || productGroupId.value || productId.value || undefined
  );
  const isPointProduct = computed(
    () => !!pointsPrice.value && pointsPrice.value > 0
  );

  const setRelatedPatternFromKit = async () => {
    relatedPattern.value =
      window.flyQualifiedProductsData?.find((product) =>
        [productTypes.CROCHET, productTypes.KNITTING].includes(product.type)
      ) ?? null;

    if (!relatedPattern.value) return;

    try {
      const { storefront } = await getApolloClients();
      const result = await storefront.query({
        query: productGroupIdByHandleQuery,
        variables: { handle: relatedPattern.value.handle }
      });

      relatedPattern.value.productGroupId =
        result.data?.product?.entityId?.value ?? null;
    } catch (error) {
      notifyBugsnagWithContext('setRelatedPatternFromKit', error, {
        message: `[DATA-ERROR] - Error on productGroupIdByHandleQuery (handle: "${relatedPattern.value.handle}")`
      });
    }
  };

  const updatePriceByQuantity = () => {
    const productQuantitiesStore = useProductQuantitiesStore();
    if (!productQuantitiesStore.bulkDiscountsAvailable) return;

    calculateNewPricesByQuantityChange();
    updateDOMPricesAndSavings();
  };

  const updatePriceByVariant = (variant: HydratedShopifyVariant) => {
    const quantityStore = useProductQuantitiesStore();

    if (quantityStore.bulkDiscountsAvailable) {
      calculateNewPricesByQuantityChange();
      updateDOMPricesAndSavings();
    } else {
      calculateNewPricesByVariantChange(variant);
      updateDOMPricesAndSavings();
    }
  };

  const patternDownloadUrl = async (
    productSku: string,
    contactUUID: string,
    countryCode: string
  ) => {
    const userStore = useUserStore();
    if (purchasable.value && !userStore.isLogged) return null;
    if (!contactUUID && !userStore.isLogged) {
      notifyBugsnagWithContext(
        'queryPatternBySku',
        `[DATA-ERROR] - Pattern download will not be tracked. No contactUUID and no session.`
      );
    }

    try {
      const { gateway } = await getApolloClients();
      const patternBySkuResponse = await gateway.query(
        queryPatternBySku(productSku, window.Shopify.locale, undefined, {
          contactUUID: contactUUID ?? undefined,
          countryCode
        })
      );

      return patternBySkuResponse.data?.patternBySku?.url ?? null;
    } catch (error) {
      notifyBugsnagWithContext('patternDownloadUrl', error, {
        message: `[DATA-ERROR] - Error on queryPatternBySku (SKU: "${productSku}", countryCode: "${countryCode}")`
      });

      return null;
    }
  };

  return {
    purchasable,
    isProduct,
    isPattern,
    productId,
    productName,
    legacyId,
    productGroupId,
    price,
    entityType,
    variantId,
    pointsPrice,
    mainImageUrl,
    relatedPattern,
    isKit,
    patternDownloadUrl,
    getStrategy,
    resolvedEntityId,
    isPointProduct,
    updatePriceByVariant,
    updatePriceByQuantity,
    setRelatedPatternFromKit
  };
});
